import React from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, Users, Utensils, Heart } from 'lucide-react';
import TitleWithLanterns from '../components/TitleWithLanterns';
import Reveal from '../components/Reveal';

const About: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-luxury-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('/restaurant_photos/apropo_photo.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-luxury-gradient" />
        <TitleWithLanterns
          title={t('about.title')}
          subtitle="Notre Histoire"
          lanternType={1}
        />
      </section>

      {/* History Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        {/* Cherry Blossom Decoration Background */}
        <img
          src="/restaurant_photos/cherry_blossom2.png"
          alt=""
          className="absolute left-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0" style={{ transform: 'translateY(-50%) scaleX(-1) scaleY(-1)' }}
        />
        <div className="section-padding relative z-10">
          <div className="max-w-4xl mx-auto">
            <Reveal>
              <h2 className="text-display-md font-display mb-6 md:mb-8 lg:mb-12 text-luxury-black text-center">
                {t('about.historyTitle')}
              </h2>
            </Reveal>
            <div className="space-y-8 text-lg text-luxury-gray-dark leading-relaxed font-body">
              <Reveal delay={0.05}><p>{t('about.historyText1')}</p></Reveal>
              <Reveal delay={0.1}><p>{t('about.historyText2')}</p></Reveal>
              <Reveal delay={0.15}><p>{t('about.historyText3')}</p></Reveal>
              <Reveal delay={0.2}>
                <p className="text-2xl font-display text-luxury-gold italic text-center mt-12">
                  {t('about.historyText4')}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Images Section */}
      <section className="py-16 bg-white relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 relative z-0">
          {[
            { src: '/restaurant_photos/_11A5133.JPG', alt: t('about.altRestaurantInterior') },
            { src: '/restaurant_photos/_11A5153.JPG', alt: t('about.altRestaurantDecor') },
            { src: '/restaurant_photos/_11A5162.JPG', alt: t('about.altInteriorDesign') },
            { src: '/restaurant_photos/_11A5149.JPG', alt: t('about.altInteriorDesign') }
          ].map((image, index) => (
            <Reveal key={image.src} delay={index * 0.08}>
              <div className="relative h-64 overflow-hidden group">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-white relative">
        {/* Gold Fan Decoration - Left Side */}
        <img
          src="/restaurant_photos/gold_fan.png"
          alt=""
          className="absolute left-0 top-1/2 -translate-y-1/2 w-64 md:w-72 lg:w-80 opacity-20 pointer-events-none z-0"
        />

        <div className="section-padding relative z-10">
          <div className="max-w-4xl mx-auto">
            <Reveal>
              <h2 className="text-display-md font-display mb-4 md:mb-6 lg:mb-8 text-luxury-black text-center">
                {t('about.philosophyTitle')}
              </h2>
              <p className="text-2xl font-display text-luxury-gold text-center mb-12 italic">
                {t('about.motto')}
              </p>
            </Reveal>
            <div className="space-y-8 text-lg text-luxury-gray-dark leading-relaxed font-body">
              <Reveal delay={0.05}><p>{t('about.philosophyText1')}</p></Reveal>
              <Reveal delay={0.1}><p>{t('about.philosophyText2')}</p></Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                <Reveal delay={0}>
                  <div className="flex items-center gap-4 luxury-card p-6 h-full">
                    <Utensils className="text-luxury-gold flex-shrink-0" size={28} />
                    <span className="font-body">{t('about.freshness')}</span>
                  </div>
                </Reveal>
                <Reveal delay={0.06}>
                  <div className="flex items-center gap-4 luxury-card p-6 h-full">
                    <Heart className="text-luxury-gold flex-shrink-0" size={28} />
                    <span className="font-body">{t('about.homemade')}</span>
                  </div>
                </Reveal>
                <Reveal delay={0.12}>
                  <div className="flex items-center gap-4 luxury-card p-6 h-full">
                    <Leaf className="text-luxury-gold flex-shrink-0" size={28} />
                    <span className="font-body">{t('about.quality')}</span>
                  </div>
                </Reveal>
                <Reveal delay={0.18}>
                  <div className="flex items-center gap-4 luxury-card p-6 h-full">
                    <Users className="text-luxury-gold flex-shrink-0" size={28} />
                    <span className="font-body">{t('about.accessible')}</span>
                  </div>
                </Reveal>
              </div>
              <Reveal delay={0.1}><p>{t('about.philosophyText3')}</p></Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        {/* Cherry Blossom Decoration Background */}
        <img
          src="/restaurant_photos/cherry_blossom1.png"
          alt=""
          className="absolute right-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0"
        />
        <div className="section-padding relative z-10">
          <div className="max-w-4xl mx-auto">
            <Reveal>
              <h2 className="text-display-md font-display mb-6 md:mb-8 lg:mb-12 text-luxury-black text-center">
                {t('about.teamTitle')}
              </h2>
            </Reveal>
            <div className="space-y-8 text-lg text-luxury-gray-dark leading-relaxed font-body">
              <Reveal delay={0.05}><p>{t('about.teamText1')}</p></Reveal>
              <Reveal delay={0.1}><p>{t('about.teamText2')}</p></Reveal>
              <Reveal delay={0.1}>
                <h3 className="text-2xl font-display text-luxury-black mt-12 mb-8">
                  {t('about.commitments')}
                </h3>
              </Reveal>
              <div className="space-y-4">
                <Reveal delay={0}>
                  <div className="luxury-card p-8">
                    <h4 className="font-display text-xl text-luxury-black mb-3">{t('about.teamCohesion').split(':')[0]}</h4>
                    <p className="font-body text-luxury-gray-dark">{t('about.teamCohesion').split(':')[1]}</p>
                  </div>
                </Reveal>
                <Reveal delay={0.08}>
                  <div className="luxury-card p-8">
                    <h4 className="font-display text-xl text-luxury-black mb-3">{t('about.environment').split(':')[0]}</h4>
                    <p className="font-body text-luxury-gray-dark">{t('about.environment').split(':')[1]}</p>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
